/**
 * Contracts List Page
 *
 * Main page for viewing and managing contracts
 * Follows the Plans pattern with filters, search, and grid/table views
 */

import {
  BILLING_PERIOD_LABELS,
  BILLING_PERIODS,
  CONTRACT_STATUS,
} from '@monorepo/shared/schemas/common'
import type { ContractResponse } from '@monorepo/shared/schemas/contracts'
import { useNavigate } from '@tanstack/react-router'
import { Plus, RefreshCw, Search } from 'lucide-react'
import { useState } from 'react'
import { PageTitle } from '@/shared/components/common/PageTitle'
import { ViewToggle } from '@/shared/components/common/tabs/ViewToggle'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import type { ContractFilters } from '../hooks/useContracts'
import { useContractFilters, useContracts } from '../hooks/useContracts'
import { ContractsList } from './ContractsList'

export function ContractsPage() {
  const navigate = useNavigate()
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table')

  const { filters, updateFilters, apiQuery } = useContractFilters()
  const { data: contractsResult, isLoading, error } = useContracts(apiQuery)

  const contracts = contractsResult?.data || []
  const meta = contractsResult?.pagination

  const handleSearch = (search: string) => {
    updateFilters({ search, page: 1 })
  }

  const handleStatusFilter = (status: string) => {
    updateFilters({
      status: status as ContractFilters['status'],
      page: 1,
    })
  }

  const handleBillingIntervalChange = (value: string) => {
    updateFilters({
      billing_interval: value as ContractFilters['billing_interval'],
      page: 1,
    })
  }

  const handleSort = (sortBy: string, sortOrder: 'asc' | 'desc') => {
    updateFilters({ sortBy, sortOrder, page: 1 })
  }

  const handlePageChange = (page: number) => {
    updateFilters({ page })
  }

  const handleContractClick = (contract: ContractResponse) => {
    navigate({ to: `/contracts/${contract.id}` })
  }

  const handleCreateClick = () => {
    navigate({ to: '/contracts/create' })
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex flex-col gap-4 md:flex-row md:justify-between md:items-start'>
        <div>
          <PageTitle
            title='Contracts'
            subtitle='Manage customer contracts and subscriptions'
          />
        </div>
        <div className='flex gap-3 items-center w-full md:w-auto'>
          <Button
            size='lg'
            className='flex-1 md:flex-initial'
            onClick={handleCreateClick}
          >
            <Plus className='w-5 h-5 mr-2' />
            Create Contract
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className='flex flex-col gap-4 md:flex-row md:items-center md:justify-between'>
        <div className='flex gap-2 items-center flex-1 max-w-4xl'>
          <div className='relative flex-1 min-w-[300px]'>
            <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4' />
            <Input
              placeholder='Search contracts...'
              value={filters.search || ''}
              onChange={e => handleSearch(e.target.value)}
              className='pl-10'
            />
          </div>

          <Select
            value={filters.status || 'all'}
            onValueChange={handleStatusFilter}
          >
            <SelectTrigger className='w-40'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All Status</SelectItem>
              <SelectItem value={CONTRACT_STATUS.draft}>Draft</SelectItem>
              <SelectItem value={CONTRACT_STATUS.active}>Active</SelectItem>
              <SelectItem value={CONTRACT_STATUS.suspended}>
                Suspended
              </SelectItem>
              <SelectItem value={CONTRACT_STATUS.expired}>Expired</SelectItem>
              <SelectItem value={CONTRACT_STATUS.terminated}>
                Terminated
              </SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={filters.billing_interval || 'all'}
            onValueChange={handleBillingIntervalChange}
          >
            <SelectTrigger className='w-44'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All Periods</SelectItem>
              {BILLING_PERIODS.map(period => (
                <SelectItem key={period} value={period}>
                  {BILLING_PERIOD_LABELS[period]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className='flex gap-2 items-center'>
          <Button variant='outline' size='sm'>
            <RefreshCw className='w-4 h-4' />
          </Button>

          <ViewToggle currentView={viewMode} onViewChange={setViewMode} />
        </div>
      </div>

      {/* Contracts List */}
      <ContractsList
        contracts={contracts}
        isLoading={isLoading}
        error={error}
        viewMode={viewMode}
        filters={filters}
        onSort={handleSort}
        onPageChange={handlePageChange}
        onContractClick={handleContractClick}
        meta={meta}
      />
    </div>
  )
}
